import { Link, usePage } from "@inertiajs/react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ListOption, SharedProps } from "@/types";
import { useDndMonitor, useDndContext, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { neighborsForReorder } from "@/lib/reorder";
import { flushOutbox, pull } from "@/lib/syncEngine";
import { useBackDismiss } from "@/hooks/useBackDismiss";
import SortableSubtaskRow from "./SortableSubtaskRow";
import type { TaskData } from "./TaskRow";
import DueDatePicker from "./DueDatePicker";
import RecurrencePicker from "./RecurrencePicker";
import VisibleFromField from "./VisibleFromField";
import LotusIcon from "./LotusIcon";
import AiActionMenu, { type AiActionEntry } from "./AiActionMenu";
import Linkified from "./Linkified";
import { todayIso } from "@/lib/dates";
import { NOTES_PLACEHOLDER } from "@/lib/notesPlaceholder";
import {
  createTask,
  toggleTask as mutateToggle,
  updateTask,
  reorderTask,
  startTemplate,
  aiRun,
  aiPreview,
  aiApply,
  type PreviewOp,
  type AiAnchor,
  type TaskPatch,
  opFields,
} from "@/mutations/tasks";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { subtasks as localSubtasks, type ViewSource } from "@/lib/views";
import { useLocalView } from "@/hooks/useLocalView";
import { useLocalTasks } from "@/hooks/useLocalTasks";
import { buildDiscussPrompt } from "@/lib/discussPrompt";
import { shareTask, prefersNativeShare, buildTargetUrl, DEFAULT_AI_CHAT_URL } from "@/lib/shareTask";
import { taskUrl } from "@/lib/taskLink";
import BucketToggle from "./BucketToggle";
import { NEEDS_NETWORK, useOnline } from "@/hooks/useOnline";

export type { ListOption } from "@/types";

function caretOffsetFromClick(
  container: HTMLElement,
  clientX: number,
  clientY: number,
  fullText: string,
): number | null {
  // Walk DOM text nodes up to caretRangeFromPoint to count characters into
  // `fullText`. <a> nodes are textual; <br> counts as one '\n'. Returns null
  // when the browser API is unavailable (jsdom) or the range is outside.
  const doc = container.ownerDocument;
  type DocWithCaret = Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  const d = doc as DocWithCaret;
  let targetNode: Node | null = null;
  let targetOffset = 0;
  if (typeof d.caretRangeFromPoint === "function") {
    const r = d.caretRangeFromPoint(clientX, clientY);
    if (r) {
      targetNode = r.startContainer;
      targetOffset = r.startOffset;
    }
  } else if (typeof d.caretPositionFromPoint === "function") {
    const p = d.caretPositionFromPoint(clientX, clientY);
    if (p) {
      targetNode = p.offsetNode;
      targetOffset = p.offset;
    }
  }
  if (!targetNode || !container.contains(targetNode)) return null;
  let offset = 0;
  let found = false;
  const walk = (node: Node) => {
    if (found) return;
    if (node === targetNode) {
      if (node.nodeType === Node.TEXT_NODE) offset += targetOffset;
      found = true;
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      offset += (node.nodeValue ?? "").length;
      return;
    }
    if ((node as Element).tagName === "BR") {
      offset += 1; // newline
      return;
    }
    node.childNodes.forEach(walk);
  };
  walk(container);
  if (!found) return null;
  return Math.min(offset, fullText.length);
}

interface Props {
  task: TaskData;
  lists: ListOption[];
  onClose: () => void;
  onDelete: (task: TaskData) => void;
  onTreeMutated?: () => void | Promise<unknown>;
}

export default function DetailsPanel({ task: rootTask, lists, onClose, onDelete, onTreeMutated }: Props) {
  const online = useOnline();
  const [stack, setStack] = useState<TaskData[]>([rootTask]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(rootTask.title);
  const [notesDraft, setNotesDraft] = useState(rootTask.notes ?? "");
  const [fetched, setFetched] = useState<{ id: string; task: TaskData; subtasks: TaskData[] } | null>(null);
  const [goneFromServer, setGoneFromServer] = useState<string | null>(null);
  // Per task id: the fields edited since the last fetch, as that fetch had them.
  const lastFromServer = useRef(new Map<string, Partial<TaskData>>());
  const [newSubtitle, setNewSubtitle] = useState("");
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [subtaskDraft, setSubtaskDraft] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [shareToast, setShareToast] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Mobile hands off via the native share sheet (header icon); desktop discusses through
  // the ✨ AI-actions menu instead — a bare "share" icon that instantly opens a chat misleads.
  const [nativeShare] = useState(() => prefersNativeShare());
  const titleRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const pendingCaretRef = useRef<number | null>(null);

  const sharedProps = usePage<SharedProps>().props;
  const aiActions = sharedProps.aiActions ?? [];
  const aiChatUrl = sharedProps.aiChatUrl ?? DEFAULT_AI_CHAT_URL;
  const discussPrompt = sharedProps.discussPrompt;
  const discussPromptEnabled = sharedProps.discussPromptEnabled ?? true;
  const taskActions = aiActions.filter((a) => a.scope === "task");
  const collectionActions = aiActions.filter((a) => a.scope === "collection");

  const headId = stack[stack.length - 1].id;
  const local = useLocalTasks("details", stack.map((t) => t.id));
  const childrenView = useCallback((source: ViewSource) => localSubtasks(source, [headId])[headId], [headId]);
  const localChildren = useLocalView("details", childrenView) as TaskData[] | null;
  const localHead = local?.rows[headId] as TaskData | undefined;
  const fallback = fetched?.id === headId ? fetched : null;
  // The local copy may lack the task (finished long ago, or not loaded yet): the server's then.
  const readsServer = !localHead && !local?.deleted.has(headId);

  const shownStack = stack.map((t, i) => {
    const fetchedTask = i === stack.length - 1 ? fallback?.task : undefined;
    return { ...t, ...fetchedTask, ...(local?.rows[t.id] as TaskData | undefined) };
  });
  const task = shownStack[shownStack.length - 1];
  const subtasks = localHead ? (localChildren ?? []) : (fallback?.subtasks ?? []);

  const visibleSubtasks = useLeavingRows(subtasks);

  // Reset stack if the prop task changes externally (different selection from list)
  useEffect(() => {
    setStack([rootTask]);
  }, [rootTask.id]);

  // The task may change under an open editor (another tab, a pull): the user's text wins,
  // and only text the user changed is saved over the change that came in.
  const titleAtEditStart = useRef("");
  const notesAtEditStart = useRef("");
  const editingTitleRef = useRef(editingTitle);
  editingTitleRef.current = editingTitle;
  const editingNotesRef = useRef(editingNotes);
  editingNotesRef.current = editingNotes;

  useEffect(() => {
    if (!editingTitleRef.current) setTitleDraft(task.title);
  }, [task.id, task.title]);

  useEffect(() => {
    if (!editingNotesRef.current) setNotesDraft(task.notes ?? "");
  }, [task.id, task.notes]);

  useEffect(() => {
    if (editingTitle) titleRef.current?.focus();
  }, [editingTitle]);

  function resizeNotes() {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  useEffect(() => {
    if (!editingNotes) return;
    const el = notesRef.current;
    if (!el) return;
    el.focus();
    const pos = pendingCaretRef.current;
    if (pos != null && pos >= 0 && pos <= el.value.length) {
      el.setSelectionRange(pos, pos);
    } else {
      const end = el.value.length;
      el.setSelectionRange(end, end);
    }
    pendingCaretRef.current = null;
    resizeNotes();
  }, [editingNotes]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    document.body.classList.add("details-open");
    return () => document.body.classList.remove("details-open");
  }, []);

  useEffect(() => {
    if (!shareToast) return;
    const t = window.setTimeout(() => setShareToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [shareToast]);

  useEffect(() => {
    if (!linkCopied) return;
    const t = window.setTimeout(() => setLinkCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [linkCopied]);

  // Reads only after the outbox is sent, so the server has the changes made here already.
  const fetchTask = useCallback(async () => {
    try {
      await flushOutbox();
      const res = await fetch(`/tasks/${headId}`, {
        headers: { Accept: "application/json" },
      });
      if (res.status === 404) setGoneFromServer(headId);
      if (!res.ok) return;
      const data = await res.json();
      lastFromServer.current.delete(headId);
      setFetched({ id: headId, task: data.task, subtasks: data.subtasks ?? [] });
    } catch {
      /* swallow */
    }
  }, [headId]);

  useEffect(() => {
    if (!readsServer) return;
    void fetchTask();
    const refetch = () => void fetchTask();
    window.addEventListener("online", refetch);
    return () => window.removeEventListener("online", refetch);
  }, [readsServer, fetchTask]);

  function refetchServerCopy() {
    if (readsServer) void fetchTask();
  }

  function patchServerCopy(id: string, change: Partial<TaskData>) {
    const apply = (t: TaskData) => (t.id === id ? { ...t, ...change } : t);
    setStack((s) => s.map(apply));
    setFetched((f) => f && { ...f, task: apply(f.task), subtasks: f.subtasks.map(apply) });
  }

  // Until the refetch (or offline, instead of it) the server's copy shows the edit made here.
  // The returned undo, for an edit that turns out not to be saved, puts back what the server
  // last sent: which of several edits since then got saved is known only after a refetch.
  function showOnServerCopy(id: string, change: Partial<TaskData> | null): () => void {
    if (!readsServer || !change) return () => {};
    const shown = [...shownStack, ...subtasks].find((t) => t.id === id);
    const fields = Object.keys(change) as (keyof TaskData)[];
    const saved = lastFromServer.current.get(id) ?? {};
    const firstEdits = fields.filter((field) => !(field in saved)).map((field) => [field, shown?.[field]]);
    lastFromServer.current.set(id, { ...saved, ...Object.fromEntries(firstEdits) });
    patchServerCopy(id, change);
    return () => {
      const server = lastFromServer.current.get(id);
      if (server) patchServerCopy(id, Object.fromEntries(fields.filter((field) => field in server).map((field) => [field, server[field]])));
    };
  }

  function showSubtaskOnServerCopy(subtask: TaskData): () => void {
    if (!readsServer) return () => {};
    setFetched((f) => {
      const own = f?.id === headId ? f : null;
      return { id: headId, task: own?.task ?? task, subtasks: [...(own?.subtasks ?? []), subtask] };
    });
    return () => setFetched((f) => f && { ...f, subtasks: f.subtasks.filter((t) => t.id !== subtask.id) });
  }

  // AI actions change the tasks on the server directly, not through the outbox.
  function refreshAfterServerChange() {
    if (readsServer) void fetchTask();
    else void pull();
  }

  const deletedFrom = stack.findIndex((t) => local?.deleted.has(t.id) || t.id === goneFromServer);
  useEffect(() => {
    if (deletedFrom === -1) return;
    if (deletedFrom === 0) onClose();
    else setStack((s) => s.slice(0, deletedFrom));
  }, [deletedFrom, onClose]);

  useDndMonitor({
    onDragEnd: (e: DragEndEvent) => {
      const { active, over } = e;
      const activeData = active.data.current as
        | { kind?: string; subtaskId?: string }
        | undefined;
      const overData = over?.data.current as
        | { kind?: string; subtaskId?: string }
        | undefined;
      if (activeData?.kind !== "detail-subtask" || overData?.kind !== "detail-subtask") return;
      if (!over || active.id === over.id) return;

      const activeId = activeData.subtaskId;
      const overId = overData.subtaskId;
      if (!activeId || !overId) return;
      const oldIndex = subtasks.findIndex((t) => t.id === activeId);
      const newIndex = subtasks.findIndex((t) => t.id === overId);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(subtasks, oldIndex, newIndex);
      const previous = subtasks;
      const showFallbackSubtasks = (rows: TaskData[]) => {
        if (readsServer) setFetched((f) => (f && f.id === headId ? { ...f, subtasks: rows } : f));
      };
      showFallbackSubtasks(reordered);
      const neighbors = neighborsForReorder(reordered, activeId);
      reorderTask(activeId, neighbors, {
        onSuccess: () => onTreeMutated?.(),
        onError: () => showFallbackSubtasks(previous),
      });
    },
  });

  function patchTask(payload: TaskPatch, opts?: { onSuccess?: () => void }) {
    let undo = () => {};
    updateTask(task.id, payload, {
      onRejected: () => undo(),
      onSuccess: () => {
        undo = showOnServerCopy(task.id, opFields(payload));
        refetchServerCopy();
        onTreeMutated?.();
        opts?.onSuccess?.();
      },
    });
  }

  async function handleShare() {
    const parentTitle = shownStack.length > 1 ? shownStack[shownStack.length - 2].title : undefined;
    const prompt = buildDiscussPrompt(task, visibleSubtasks, parentTitle, {
      enabled: discussPromptEnabled,
      custom: discussPrompt,
    });
    const result = await shareTask(prompt, aiChatUrl);
    if (result === "copied-opened") setShareToast("Промпт скопирован, открываю чат");
    else if (result === "copied-blank") setShareToast("Промпт скопирован — вставь в чат");
  }

  function deliverPrompt(promptTemplate: string): string {
    const parentTitle = shownStack.length > 1 ? shownStack[shownStack.length - 2].title : undefined;
    return buildDiscussPrompt(task, visibleSubtasks, parentTitle, {
      enabled: true,
      custom: promptTemplate,
      link: task.listId ? taskUrl(task.listId, task.id) : undefined,
    });
  }

  async function copyChannel(promptTemplate: string) {
    const prompt = deliverPrompt(promptTemplate);
    if (prefersNativeShare()) {
      try { await navigator.share({ text: prompt }); return; } catch { /* fall through */ }
    }
    try { await navigator.clipboard.writeText(prompt); setShareToast("Промпт скопирован"); }
    catch { /* clipboard denied (non-HTTPS) */ }
  }

  function webChannel(promptTemplate: string) {
    const prompt = deliverPrompt(promptTemplate);
    const { url, prefilled } = buildTargetUrl(aiChatUrl, prompt);
    void navigator.clipboard?.writeText(prompt).catch(() => {});
    window.open(url, "_blank", "noopener");
    setShareToast(prefilled ? "Открываю чат" : "Промпт скопирован — вставь в чат");
  }

  async function handleCopyLink() {
    if (!task.listId) return;
    try {
      await navigator.clipboard.writeText(taskUrl(task.listId, task.id));
      setLinkCopied(true);
    } catch {
      // clipboard denied (e.g. non-HTTPS context) — nothing to show
    }
  }

  function startEditingTitle() {
    titleAtEditStart.current = task.title;
    setTitleDraft(task.title);
    setEditingTitle(true);
  }

  function saveTitle() {
    setEditingTitle(false);
    const next = titleDraft.trim();
    if (!next || next === titleAtEditStart.current) {
      setTitleDraft(task.title);
      return;
    }
    patchTask({ title: next });
  }

  function startEditingNotes() {
    notesAtEditStart.current = task.notes ?? "";
    setNotesDraft(task.notes ?? "");
    setEditingNotes(true);
  }

  function saveNotes() {
    if (notesDraft === notesAtEditStart.current) {
      setNotesDraft(task.notes ?? "");
      return;
    }
    patchTask({ notes: notesDraft });
  }

  // One ✨ menu for the task, anchored on it: task-scoped actions (break into subtasks)
  // plus, once there are enough subtasks to be worth it, collection-scoped ones (e.g. dedupe).
  const aiAnchor: AiAnchor = { kind: "task", id: task.id };
  const aiEntry = (a: {
    id: string; name: string; confirm: boolean; prompt_template: string;
    can_grok: boolean; can_web: boolean; can_copy: boolean;
  }): AiActionEntry => ({
    id: a.id,
    name: a.name,
    confirm: a.confirm,
    ...(a.can_grok ? {
      run: () => aiRun(aiAnchor, a.id, { onChange: refreshAfterServerChange }),
      preview: () => aiPreview(aiAnchor, a.id),
      apply: (ops: PreviewOp[]) => aiApply(aiAnchor, ops, { onChange: refreshAfterServerChange }),
    } : {}),
    ...(a.can_web ? { web: () => webChannel(a.prompt_template) } : {}),
    ...(a.can_copy ? { copy: () => copyChannel(a.prompt_template) } : {}),
  });
  const aiEntries: AiActionEntry[] = [
    ...taskActions.map(aiEntry),
    ...(visibleSubtasks.length > 1 ? collectionActions.map(aiEntry) : []),
  ];

  function moveToList(listId: string) {
    patchTask({ list_id: listId }, { onSuccess: onClose });
  }

  // A recurring task is not completed: the server moves it to its next occurrence.
  function toggled(t: TaskData): Partial<TaskData> | null {
    if (t.status === "completed") return { status: "pending", completedAt: null };
    return t.recurrenceRule ? null : { status: "completed", completedAt: new Date().toISOString() };
  }

  function toggleStatus(t: TaskData = task) {
    const advancesRecurrence = t.status !== "completed" && Boolean(t.recurrenceRule);
    let undo = () => {};
    mutateToggle(t, {
      onRejected: () => undo(),
      onSuccess: () => {
        onTreeMutated?.();
        if (advancesRecurrence) {
          dismissPanel();
          return;
        }
        undo = showOnServerCopy(t.id, toggled(t));
        refetchServerCopy();
      },
    });
  }

  function toggleSubtask(child: TaskData) {
    let undo = () => {};
    mutateToggle(child, {
      onRejected: () => undo(),
      onSuccess: () => {
        undo = showOnServerCopy(child.id, toggled(child));
        onTreeMutated?.();
        refetchServerCopy();
      },
    });
  }

  function drillInto(child: TaskData) {
    setStack((s) => [...s, child]);
  }

  function startEditSubtask(child: TaskData) {
    setEditingSubtaskId(child.id);
    setSubtaskDraft(child.title);
  }

  function saveSubtaskTitle(child: TaskData) {
    const next = subtaskDraft.trim();
    setEditingSubtaskId(null);
    if (!next || next === child.title) return;
    let undo = () => {};
    updateTask(child.id, { title: next }, {
      onRejected: () => undo(),
      onSuccess: () => {
        undo = showOnServerCopy(child.id, { title: next });
        refetchServerCopy();
        onTreeMutated?.();
      },
    });
  }

  function popStack() {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }

  const dnd = useDndContext();
  const swipeRef = useRef<{ startX: number; startY: number; locked: "h" | "v" | null; currentX: number } | null>(null);
  const [swipeX, setSwipeX] = useState(0);
  const [swipeAnimating, setSwipeAnimating] = useState(false);

  function dismissPanel() {
    if (stack.length > 1) popStack();
    else onClose();
  }

  useBackDismiss(dismissPanel, () => stack.length > 1);

  function onPanelPointerDown(e: React.PointerEvent) {
    if (e.pointerType !== "touch") return;
    if (dnd.active) return; // a held subtask drag wins
    if ((e.target as HTMLElement).closest("input, textarea")) return;
    swipeRef.current = { startX: e.clientX, startY: e.clientY, locked: null, currentX: 0 };
    setSwipeAnimating(false);
  }

  function onPanelPointerMove(e: React.PointerEvent) {
    const g = swipeRef.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.locked) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      g.locked = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
      if (g.locked === "v") {
        swipeRef.current = null;
        return;
      }
    }
    g.currentX = Math.max(0, dx);
    setSwipeX(g.currentX);
  }

  function onPanelPointerUp() {
    const g = swipeRef.current;
    if (!g) return;
    swipeRef.current = null;
    setSwipeAnimating(true);
    if (g.locked === "h" && g.currentX > 80) {
      setSwipeX(0);
      dismissPanel();
      return;
    }
    setSwipeX(0);
  }

  function submitSubtask(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newSubtitle.trim();
    if (!trimmed || !task.listId) return;
    let undo = () => {};
    const id = createTask({ title: trimmed, parentId: task.id, listId: task.listId }, { onRejected: () => undo() });
    if (!id) return;
    undo = showSubtaskOnServerCopy({ id, title: trimmed, status: "pending", position: "", completedAt: null, parentId: task.id, listId: task.listId });
    setNewSubtitle("");
    refetchServerCopy();
    onTreeMutated?.();
  }

  const isDone = task.status === "completed";
  const isSubtask = (task.parentId ?? null) !== null;
  const completedSubtaskCount = visibleSubtasks.filter((c) => c.status === "completed").length;
  const inMyDay = task.myDayOn === todayIso();
  const myDayLabel = inMyDay ? "Убрать из «Мой день»" : "В «Мой день»";
  const created = task.createdAt ? new Date(task.createdAt) : null;
  const createdLabel = created
    ? new Intl.DateTimeFormat("ru-RU", { month: "short", day: "numeric", year: "numeric" }).format(created)
    : null;

  return (
    <>
      <div className="details-backdrop md:hidden" />
      <aside
        className="details-panel"
        data-testid="details-panel"
        onPointerDown={onPanelPointerDown}
        onPointerMove={onPanelPointerMove}
        onPointerUp={onPanelPointerUp}
        onPointerCancel={() => {
          swipeRef.current = null;
          setSwipeAnimating(true);
          setSwipeX(0);
        }}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swipeAnimating ? "transform 150ms ease-out" : "none",
        }}
      >
        <header className="details-header">
          {stack.length > 1 && (
            <button
              type="button"
              aria-label="Назад"
              onClick={popStack}
              className="details-close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <button
            type="button"
            aria-label={isDone ? "Вернуть в активные" : "Завершить задачу"}
            onClick={() => toggleStatus()}
            className={`task-checkbox ${isDone ? "task-checkbox-done" : ""}`}
          >
            {isDone && (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          {editingTitle ? (
            <input
              ref={titleRef}
              type="text"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  saveTitle();
                } else if (e.key === "Escape") {
                  setTitleDraft(task.title);
                  setEditingTitle(false);
                }
              }}
              className="details-title-input"
            />
          ) : (
            <h3
              className={`details-title ${isDone ? "task-row-done" : ""}`}
              onClick={startEditingTitle}
            >
              <Linkified text={task.title} />
            </h3>
          )}

          {task.listId && (
            <button
              type="button"
              aria-label={linkCopied ? "Ссылка скопирована" : "Скопировать ссылку на задачу"}
              title={linkCopied ? "Ссылка скопирована" : "Скопировать ссылку на задачу"}
              onClick={handleCopyLink}
              className="details-close"
              data-testid="copy-link"
            >
              {linkCopied ? (
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                </svg>
              )}
            </button>
          )}

          {nativeShare && (
            <button
              type="button"
              aria-label="Обсудить с ИИ"
              title="Обсудить с ИИ"
              onClick={handleShare}
              className="details-close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          )}

          <button
            type="button"
            aria-label="Закрыть"
            onClick={onClose}
            className="details-close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="details-body">
          <div className="flex gap-2">
            {/* A template is a re-runnable checklist, so My Day pinning doesn't apply to it. */}
            {!task.reusable && (
              <button
                type="button"
                onClick={() => patchTask({ my_day_on: inMyDay ? null : todayIso() })}
                aria-label={myDayLabel}
                title={myDayLabel}
                aria-pressed={inMyDay}
                className="details-field w-auto flex-shrink-0"
                data-testid="my-day-toggle"
              >
                <svg
                  className={`w-4 h-4 flex-shrink-0 ${inMyDay ? "text-blue-500" : "text-gray-400"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </button>
            )}
            {/* Shown for a subtask too — that is the only way to promote one on mobile,
                where the sidebar (and its drop targets) sits behind the hamburger. */}
            <div className="flex-1 min-w-0">
              <BucketToggle buckets={lists} currentId={task.listId} isSubtask={isSubtask} onPick={moveToList} />
            </div>
          </div>

          {/* A template is just a re-runnable checklist — a schedule doesn't apply to the
              master, so these controls hide while it's reusable. */}
          {!task.reusable && (
            <>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <DueDatePicker
                    value={task.dueOn ?? null}
                    onChange={(next) => patchTask({ due_on: next })}
                  />
                </div>
                <div className="flex-shrink-0">
                  <VisibleFromField
                    value={task.visibleFrom ?? null}
                    onChange={(next) => patchTask({ visible_from: next })}
                  />
                </div>
              </div>

              <RecurrencePicker
                value={task.recurrenceRule ?? null}
                onChange={(next) => {
                  const payload: TaskPatch = { recurrence_rule: next };
                  if (next && !task.dueOn) payload.due_on = todayIso();
                  patchTask(payload);
                }}
              />

            </>
          )}

          {!isSubtask && task.reusable && (
            <button
              type="button"
              onClick={() => startTemplate(task.id, { onDone: onClose })}
              disabled={!online}
              title={online ? undefined : NEEDS_NETWORK}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="start-template"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Начать</span>
            </button>
          )}

          {editingNotes ? (
            <textarea
              ref={notesRef}
              value={notesDraft}
              onChange={(e) => {
                setNotesDraft(e.target.value);
                resizeNotes();
              }}
              onBlur={() => {
                saveNotes();
                setEditingNotes(false);
              }}
              placeholder={NOTES_PLACEHOLDER}
              className="details-notes"
              style={{ minHeight: "6rem", overflow: "hidden" }}
              rows={1}
            />
          ) : (
            <div
              className="details-notes details-notes-view"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("a")) return;
                pendingCaretRef.current = caretOffsetFromClick(
                  e.currentTarget,
                  e.clientX,
                  e.clientY,
                  notesDraft,
                );
                startEditingNotes();
              }}
              data-testid="notes-view"
            >
              {notesDraft ? (
                <Linkified text={notesDraft} />
              ) : (
                <span className="details-notes-placeholder">{NOTES_PLACEHOLDER}</span>
              )}
            </div>
          )}

          <div className="subtasks-block">
            <div className="flex items-center gap-1 mb-1">
              <h4 className="subtasks-title mb-0">Подзадачи</h4>
              {visibleSubtasks.length > 0 && (
                <span className="subtask-count mr-1" data-testid="subtasks-progress">
                  {completedSubtaskCount}/{visibleSubtasks.length}
                </span>
              )}
              <AiActionMenu
                entries={aiEntries}
                testId="ai-actions-menu"
                buttonClassName="subtasks-action disabled:cursor-default disabled:opacity-60"
                iconClassName="w-4 h-4"
                spinnerClassName="block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin"
              />
              {visibleSubtasks.length > 0 && !task.reusable && (
                <Link
                  href={`/zen?task_id=${task.id}`}
                  aria-label="Дзен-режим"
                  title="Дзен-режим"
                  className="subtasks-action"
                >
                  <LotusIcon className="w-4 h-4" />
                </Link>
              )}
            </div>
            <SortableContext
              items={visibleSubtasks.map((c) => `detail:${c.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {visibleSubtasks.map((child) => (
                <SortableSubtaskRow
                  key={child.id}
                  child={child}
                  isEditing={editingSubtaskId === child.id}
                  draft={subtaskDraft}
                  onDraftChange={setSubtaskDraft}
                  onStartEdit={() => startEditSubtask(child)}
                  onSaveEdit={() => saveSubtaskTitle(child)}
                  onCancelEdit={() => setEditingSubtaskId(null)}
                  onToggle={() => toggleSubtask(child)}
                  onDrill={() => drillInto(child)}
                />
              ))}
            </SortableContext>

            <form onSubmit={submitSubtask} className="mt-2">
              <div className="task-input-row pl-1">
                <svg className="w-5 h-4 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                </svg>
                <input
                  type="text"
                  value={newSubtitle}
                  onChange={(e) => setNewSubtitle(e.target.value)}
                  placeholder="Новая подзадача…"
                  className="task-input-bare px-0 py-1.5"
                />
                <button
                  type="submit"
                  disabled={!newSubtitle.trim()}
                  className="px-3 py-1 text-xs rounded-md text-blue-600 hover:bg-blue-50 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-default cursor-pointer dark:text-blue-400 dark:hover:bg-blue-950 dark:disabled:text-gray-500 dark:disabled:hover:bg-transparent"
                >
                  Добавить
                </button>
              </div>
            </form>
          </div>
        </div>

        <footer className="details-footer">
          {createdLabel && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Создано {createdLabel}
            </span>
          )}
          <button
            type="button"
            onClick={() => onDelete(task)}
            aria-label="Удалить задачу"
            title="Удалить задачу"
            className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </footer>
      </aside>

      {shareToast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-md bg-slate-900 text-slate-50 px-3 py-2 text-sm shadow-lg">
          {shareToast}
        </div>
      )}
    </>
  );
}
